import { useSheet } from "@/elements/components/shared/sheet-provider";
import { Button } from "@/elements/components/ui/button";
import { CheckboxInput } from "@/elements/components/ui/checkbox-input";
import { api } from "@/elements/components/utils/convex-api";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import React, { useCallback, useState } from "react";
import { Text, View } from "react-native";
import { useAuthActions } from "@convex-dev/auth/react";
import { AppBar } from "@/elements/components/ui/app-bar";
import ResponsiveContent from "@/elements/components/shared/responsive-content";

function ParentLegalScreen() {
  const { signOut } = useAuthActions();
  const { data: profile } = useQuery(
    convexQuery(api.parent.onboarding.queries.getProfile, {})
  );
  const [isParentalTermsOfServiceChecked, setIsParentalTermsOfServiceChecked] =
    useState(false);
  const [isStudentTermsOfServiceChecked, setIsStudentTermsOfServiceChecked] =
    useState(false);
  const [isDataProtectionPolicyChecked, setIsDataProtectionPolicyChecked] =
    useState(false);
  const [
    isConsentToRecordSessionsChecked,
    setIsConsentToRecordSessionsChecked,
  ] = useState(false);
  const { open } = useSheet<any, any>(SheetIds.LEGAL);
  const { data: parentalTermsOfService } = useQuery({
    ...convexQuery(api.parent.onboarding.queries.getLegalDocument, {
      documentType: "parent-terms-and-conditions",
    }),
    enabled: !!profile,
  });
  const { data: studentTermsOfService } = useQuery({
    ...convexQuery(api.parent.onboarding.queries.getLegalDocument, {
      documentType: "student-terms-and-conditions",
    }),
    enabled: !!profile,
  });
  const { data: dataProtectionPolicy } = useQuery({
    ...convexQuery(api.parent.onboarding.queries.getLegalDocument, {
      documentType: "data-protection",
    }),
    enabled: !!profile,
  });
  const { data: consentToRecordSessions } = useQuery({
    ...convexQuery(api.parent.onboarding.queries.getLegalDocument, {
      documentType: "consent-to-record-sessions",
    }),
    enabled: !!profile,
  });
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(api.parent.onboarding.mutations.update),
  });
  const router = useRouter();

  const handleSubmit = useCallback(async () => {
    await updateProfile({
      dataProtectionPolicyAcceptedAt: Date.now(),
      parentalTermsAndConditionsAcceptedAt: Date.now(),
      studentTermsAndConditionsAcceptedAt: Date.now(),
      consentToRecordSessionsAcceptedAt: Date.now(),
      onboardingCompletedAt: Date.now(),
      onboardingStepCompleted: "legal",
    });
    router.replace("/(app)/home");
  }, [router, updateProfile]);

  const onParentalTermsOfServicePress = useCallback(async () => {
    const payload = {
      title: "Parental Terms and Conditions",
      content: parentalTermsOfService,
      status: isParentalTermsOfServiceChecked ? "accepted" : "pending",
    };
    const result = await open(payload);
    if (result?.status) {
      setIsParentalTermsOfServiceChecked(result.status === "accepted");
    }
  }, [open, isParentalTermsOfServiceChecked, parentalTermsOfService]);

  const onStudentTermsOfServicePress = useCallback(async () => {
    const payload = {
      title: "Student Terms and Conditions",
      content: studentTermsOfService,
      status: isStudentTermsOfServiceChecked ? "accepted" : "pending",
    };
    const result = await open(payload);
    if (result?.status) {
      setIsStudentTermsOfServiceChecked(result.status === "accepted");
    }
  }, [open, isStudentTermsOfServiceChecked, studentTermsOfService]);

  const onDataProtectionPolicyPress = useCallback(async () => {
    const payload = {
      title: "Data Protection Policy",
      content: dataProtectionPolicy,
      status: isDataProtectionPolicyChecked ? "accepted" : "pending",
    };
    const result = await open(payload);
    if (result?.status) {
      setIsDataProtectionPolicyChecked(result.status === "accepted");
    }
  }, [open, isDataProtectionPolicyChecked, dataProtectionPolicy]);

  const onConsentToRecordSessionsPress = useCallback(async () => {
    const payload = {
      title: "Consent to record sessions",
      content: consentToRecordSessions,
      status: isConsentToRecordSessionsChecked ? "accepted" : "pending",
    };
    const result = await open(payload);
    if (result?.status) {
      setIsConsentToRecordSessionsChecked(result.status === "accepted");
    }
  }, [open, isConsentToRecordSessionsChecked, consentToRecordSessions]);

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon
          onPress={() => (router.canGoBack() ? router.back() : signOut())}
        />
        <AppBar.Title>{router.canGoBack() ? "Back" : "Sign out"}</AppBar.Title>
      </AppBar>
      <ResponsiveContent>
        <View className="mx-5">
          <View className="h-4" />
          <View className="gap-1">
            <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
              Almost there..
            </Text>
            <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
              We need your consent to proceed.
            </Text>
          </View>
          <View className="h-8" />
          <View className="gap-4">
            <Text className="text-base leading-[1.5] font-geologica-medium font-medium text-black">
              I have read and agree to the following.
            </Text>
            <View className="gap-2">
              <CheckboxInput onPress={onParentalTermsOfServicePress}>
                <CheckboxInput.Label size="lg">
                  Parental Terms and Conditions
                </CheckboxInput.Label>
                <CheckboxInput.Icon checked={isParentalTermsOfServiceChecked} />
              </CheckboxInput>

              <CheckboxInput onPress={onStudentTermsOfServicePress}>
                <CheckboxInput.Label size="lg">
                  Student Terms and Conditions
                </CheckboxInput.Label>
                <CheckboxInput.Icon checked={isStudentTermsOfServiceChecked} />
              </CheckboxInput>

              <CheckboxInput onPress={onDataProtectionPolicyPress}>
                <CheckboxInput.Label size="lg">
                  Data Protection Policy
                </CheckboxInput.Label>
                <CheckboxInput.Icon checked={isDataProtectionPolicyChecked} />
              </CheckboxInput>

              <CheckboxInput onPress={onConsentToRecordSessionsPress}>
                <CheckboxInput.Label size="lg">
                  Consent to record sessions
                </CheckboxInput.Label>
                <CheckboxInput.Icon
                  checked={isConsentToRecordSessionsChecked}
                />
              </CheckboxInput>
            </View>
          </View>
          <View className="h-8" />
          <Button
            className="w-full"
            intent="primary"
            disabled={
              !isParentalTermsOfServiceChecked ||
              !isStudentTermsOfServiceChecked ||
              !isDataProtectionPolicyChecked
            }
            onPress={handleSubmit}
            size="md"
          >
            <Button.Text size="md">Create Account</Button.Text>
          </Button>
        </View>
      </ResponsiveContent>
    </View>
  );
}

export default ParentLegalScreen;
